import { marked } from "marked";
import DOMPurify from "dompurify";

/**
 * Markdown -> sanitized HTML, the single renderer behind the blog editor's live preview,
 * the admin article preview and the public article page.
 *
 * The source is written by a super admin and shown to every visitor, so the output is
 * always passed through DOMPurify after `marked`: `marked` does not sanitize (raw HTML in
 * the source passes straight through it), and DOMPurify's default profile removes script
 * elements, event-handler attributes and script URLs. This module never returns `marked`'s
 * output directly.
 *
 * The profile is DOMPurify's HTML-only profile (`USE_PROFILES: {html: true}`: no SVG, no
 * MathML) narrowed to what an article needs:
 *  - no `<style>`, and none of the elements that build interaction or load another
 *    document or media (`form`, `input`, `button`, `textarea`, `select`, `iframe`,
 *    `object`, `embed`, `picture`, `source`, `video`, `audio`, `track`, `map`, `area`: an image map is a link
 *    the rules below would otherwise not see): an article
 *    restyles nothing, asks for nothing and embeds nothing;
 *  - none of the attributes that name a SECOND resource or inject CSS (`srcset`, `sizes`,
 *    `style`, `background`, `poster`, `ping`), so an `<img>` loads from its `src` alone;
 *  - an `<img>` survives only with an absolute `http(s)://` `src`, or a path that starts
 *    with exactly one `/` followed by something other than `/` or `\`: not a relative name
 *    (it would resolve against the article URL), not a protocol-relative URL (browsers
 *    read `/\` as `//`), not a `data:` URI, and not a missing/empty source, which only
 *    shows a broken-image icon. A backslash or a control character (a browser strips a tab
 *    or newline from a URL, so `/<tab>/host` is `//host`) anywhere in it is refused;
 *  - an `<a href>` with a backslash or a control character is stripped of its `href` (the
 *    text stays): `/\host` would be an external link that skipped the rule below;
 *  - external links (`http(s)://` and protocol-relative) open in a new tab with
 *    `rel="noopener noreferrer"`; in-site links are left alone.
 */

// A private instance, so the link hook below cannot leak into any other sanitize call.
const purifier = DOMPurify(window);

const FORBIDDEN_TAGS = [
  "style", "form", "input", "button", "textarea", "select", "iframe", "object", "embed",
  "picture", "source", "video", "audio", "track", "map", "area",
];
const FORBIDDEN_ATTRS = ["srcset", "sizes", "style", "background", "poster", "ping"];
// A backslash or any C0 control character / space: a browser rewrites `\` to `/` and strips
// tab, newline and CR from a URL, so none of them can be allowed to hide a second slash.
const UNSAFE_URL_CHARS = /[\\\u0000-\u0020\u007f]/;
// Absolute http(s), or ONE root-relative path (a second `/` or a `\` would be read as a host).
const ALLOWED_IMAGE_SRC = /^(?:https?:\/\/|\/(?![/\\]))/i;

purifier.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "IMG") {
    const src = (node.getAttribute("src") || "").trim();
    if (!ALLOWED_IMAGE_SRC.test(src) || UNSAFE_URL_CHARS.test(src)) node.remove();
    return;
  }
  if (node.tagName !== "A") return;
  const href = node.getAttribute("href") || "";
  if (UNSAFE_URL_CHARS.test(href.trim())) {
    node.removeAttribute("href");
    return;
  }
  if (/^(https?:)?\/\//i.test(href)) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

/**
 * Sanitizes untrusted HTML with the blog's DOMPurify profile (see the module comment).
 *
 * @param {string} html
 * @returns {string}
 */
export const sanitizeHtml = (html) => purifier.sanitize(html, {
  USE_PROFILES: { html: true },
  FORBID_TAGS: FORBIDDEN_TAGS,
  FORBID_ATTR: FORBIDDEN_ATTRS,
});

/**
 * Markdown to UNSANITIZED HTML. Never display this; it exists so a caller that renders
 * with `dangerouslySetInnerHTML` can put `sanitizeHtml` at the point of use
 * (components/MarkdownBody.jsx). Everything else wants `renderMarkdown`.
 *
 * @param {string | null | undefined} source Markdown text.
 * @returns {string} `""` for an empty or non-string source.
 */
export function parseMarkdown(source) {
  if (typeof source !== "string" || !source.trim()) return "";
  return marked.parse(source, { async: false, gfm: true });
}

/**
 * @param {string | null | undefined} source Markdown text.
 * @returns {string} sanitized HTML, `""` for an empty or non-string source.
 */
export const renderMarkdown = (source) => sanitizeHtml(parseMarkdown(source));

/**
 * The visible text of a Markdown source, whitespace collapsed — what a meta description
 * is made of.
 *
 * @param {string | null | undefined} source Markdown text.
 * @returns {string}
 */
export function markdownToPlainText(source) {
  const doc = new DOMParser().parseFromString(renderMarkdown(source), "text/html");
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
}
